import { useFormik } from 'formik';
import cloneDeep from 'lodash/cloneDeep';
import set from 'lodash/set';
import React from 'react';

import type { RemapFormValues } from '../components/AccountRemappingModal.model';
import {
  AccountRemappingSchema,
  createRemapConfig,
} from '../components/AccountRemappingModal.model';
import { useRemapAccountsRequest } from './useRemapAccountsRequest';
import { useRemapAccountsState } from './useRemapAccountsState';

export const useRemapAccountFlow = ({ currentAccountSubdomain, onSubmit }) => {
  const [remapError, setRemapError] = React.useState<string | null>(null);
  const {
    onRemapAccounts,
    error: remapRequestError,
    isLoading,
    clearError,
  } = useRemapAccountsRequest();
  const formik = useFormik<RemapFormValues>({
    initialValues: {
      accountConfigs: [],
    },
    enableReinitialize: true,
    validateOnBlur: true,
    validateOnChange: false,
    validationSchema: AccountRemappingSchema,
    onSubmit,
  });
  const { values, setFieldValue, validateForm, isValid } = formik;
  const { accounts, accountError, fetchAccountByCmgEntityKey, fetchFirmByCmgEntityKey } =
    useRemapAccountsState({ currentAccountSubdomain });

  const onEntityKeyChange = React.useCallback(
    async (cmgEntityKey, baseFieldName) => {
      try {
        if (cmgEntityKey.length !== 9) {
          await setFieldValue(`${baseFieldName}.newConfig.cmgEntityName`, '');
          await setFieldValue(`${baseFieldName}.newConfig.subdomain`, '');
          await setFieldValue(`${baseFieldName}.newConfig.loadingEntityName`, false);
          await validateForm();
          setRemapError(null);
          return;
        }
        await setFieldValue(`${baseFieldName}.newConfig.loadingEntityName`, true);
        const account = await fetchAccountByCmgEntityKey(cmgEntityKey);
        const firm = await fetchFirmByCmgEntityKey(cmgEntityKey);

        if (firm) {
          await setFieldValue(`${baseFieldName}.newConfig.cmgEntityName`, firm.displayName);
        } else {
          await setFieldValue(`${baseFieldName}.newConfig.cmgEntityName`, '');
          await setFieldValue(`${baseFieldName}.newConfig.loadingEntityName`, false);
          setRemapError('CMG Entity Key is not found');
          /**
           * validateForm() defaults to Formik's own internal values snapshot, which can be
           * stale here since React may not have re-rendered yet between the setFieldValue
           * calls above; pass the up-to-date values explicitly to avoid validating against
           * the pre-update state.
           */
          const updatedValues = set(
            cloneDeep(values),
            `${baseFieldName}.newConfig.cmgEntityName`,
            ''
          );
          await validateForm(updatedValues);
          return;
        }

        if (!account) {
          await setFieldValue(`${baseFieldName}.newConfig.loadingEntityName`, false);
          await setFieldValue(`${baseFieldName}.newConfig.subdomain`, '');
          await validateForm();
          setRemapError(null);
          return;
        }
        await setFieldValue(`${baseFieldName}.newConfig.loadingEntityName`, false);
        await setFieldValue(`${baseFieldName}.newConfig.subdomain`, account.subdomain);
        await validateForm();

        const configSubdomainMismatch =
          account.subdomain !== '' &&
          !!values.accountConfigs.find(config => {
            return config.currentConfig.subdomain !== account.subdomain;
          });

        if (configSubdomainMismatch) {
          setRemapError('The new CMG Entity Key is already associated with an existing account.');
        } else {
          setRemapError(null);
        }
      } catch {
        setRemapError('Failure when trying to remap account');
        await setFieldValue(`${baseFieldName}.newConfig.loadingEntityName`, false);
      }
    },
    [
      setFieldValue,
      validateForm,
      setRemapError,
      fetchAccountByCmgEntityKey,
      fetchFirmByCmgEntityKey,
      values,
    ]
  );

  const onRemapAccountConfigs = React.useCallback(async () => {
    await onRemapAccounts(values.accountConfigs);
    onSubmit();
  }, [onRemapAccounts, values, onSubmit]);

  React.useEffect(() => {
    (accounts ?? []).forEach((account, index) => {
      if (!account) {
        return;
      }

      const exisingRemapConfig = values.accountConfigs[index];
      if (!exisingRemapConfig) {
        void setFieldValue(
          `accountConfigs.${index}`,
          createRemapConfig({
            cmgEntityName: account.name,
            subdomain: account.subdomain,
            cmgEntityKey: account.cmgEntityKey,
            id: account.id,
          })
        );
      }
    });
  }, [values, setFieldValue, accounts]);

  const error = remapError || accountError;

  return {
    onEntityKeyChange,
    formik,
    error,
    isValid,
    onRemapAccountConfigs,
    remapRequestError,
    isLoadingRemapRequest: isLoading,
    clearError,
  };
};
